import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Logo } from '../../components/Logo';
import { ThemeToggle } from '../../components/ThemeToggle';
import './landing.css';

/**
 * The marketing home page, in the Ember treatment (CLAUDE.md §6.2): extended silver capitals,
 * burnt-orange labels and light beams, outlined boxes that fade out, "+" corner marks.
 *
 * Every claim here describes something the product does today; the one roadmap item is labelled
 * as next. No invented metrics (§6.2) — the console preview is labelled illustrative, and its CVE
 * facts are public ones (CVE-2020-11023 is in CISA KEV; NVD scores it CVSS 3.1 6.1).
 */

const standards = ['OWASP Top 10', 'MITRE CWE', 'CVSS v4.0', 'FIRST EPSS', 'CISA KEV', 'OSV'];

const steps = [
  {
    n: '01',
    title: 'Scope',
    body: 'Name the hosts, the test window and the rate limit. A named approver signs it, and the scope is hash-pinned so any later change shows.',
  },
  {
    n: '02',
    title: 'Discover',
    body: 'A passive scan maps what the application exposes, and read-only cloud discovery maps what runs behind it.',
  },
  {
    n: '03',
    title: 'Prioritise',
    body: 'Every finding gets its CWE, OWASP category and CVE facts. Known exploitation outranks likely exploitation, which outranks severity.',
  },
  {
    n: '04',
    title: 'Decide',
    body: 'Analysts triage with the agent’s cited suggestions, and an approver signs a report whose integrity is checked on every read.',
  },
];

const changes = [
  {
    n: '01',
    title: 'Applications change weekly',
    body: 'An annual penetration test describes an application that no longer exists.',
  },
  {
    n: '02',
    title: 'Exploitation is faster',
    body: 'CISA’s KEV catalogue grows as attackers turn new flaws into working attacks.',
  },
  {
    n: '03',
    title: 'AI can do the routine work',
    body: 'Only if every claim it makes can be traced to a source a person can check.',
  },
];

const helps = [
  {
    label: 'Applications',
    items: ['Approved passive scanning', 'A live surface map', 'Endpoints no one owns'],
  },
  {
    label: 'Cloud',
    items: [
      'Read-only AWS discovery',
      'Real architecture, with evidence',
      'Blast radius of every role',
    ],
  },
  {
    label: 'Vulnerabilities',
    items: ['CVE and CVSS from NVD', 'EPSS and CISA KEV', 'The version that fixes it'],
  },
];

const benefits = [
  {
    n: '01',
    title: 'Know what is exposed',
    body: 'Scans and cloud discovery meet on one map, so surface no team owns stands out.',
  },
  {
    n: '02',
    title: 'Fix what is exploited first',
    body: 'Known exploitation, then likely exploitation, then severity — with the reasons shown.',
  },
  {
    n: '03',
    title: 'Prove it to an auditor',
    body: 'Hash-pinned scopes and reports, named approvals and an append-only audit trail.',
  },
  {
    n: '04',
    title: 'Keep the AI honest',
    body: 'Every AI analysis cites its sources. One that cites anything it cannot ground is discarded.',
  },
  {
    n: '05',
    title: 'Stay in control',
    body: 'Nothing intrusive runs without a recorded, named approval for that gate.',
  },
];

const comparison = [
  ['A yearly test report', 'Continuous, approved scanning'],
  ['CVSS on its own', 'CISA KEV, EPSS and CVSS together'],
  ['AI you have to trust', 'AI that cites its sources'],
  ['Evidence in spreadsheets', 'An audit trail written as it happens'],
];

/** The template's light beam: a trapezoid lit at one edge, fading out. */
function Beam({ side }: { side: 'left' | 'right' | 'top' }) {
  return <span className={`lp-beam lp-beam-${side}`} aria-hidden="true" />;
}

/** The template's "+" corner mark. */
function Plus({ className = '' }: { className?: string }) {
  return (
    <span className={`lp-plus ${className}`} aria-hidden="true">
      +
    </span>
  );
}

/** The orange ↘ arrow and its tagline. */
function ArrowTag({ children }: { children: ReactNode }) {
  return (
    <p className="lp-arrowtag">
      <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden="true">
        <path d="M4 4 L17 17 M17 6 V17 H6" fill="none" stroke="var(--brand)" strokeWidth="2.4" />
      </svg>
      <span>{children}</span>
    </p>
  );
}

function NumberedList({ items }: { items: { n: string; title: string; body: string }[] }) {
  return (
    <ol className="lp-numbered">
      {items.map((s) => (
        <li key={s.n}>
          <span className="label">
            {s.n} — {s.title}
          </span>
          <p>{s.body}</p>
        </li>
      ))}
    </ol>
  );
}

/** An illustrative slice of the console, in place of the template's photographs. */
function ConsolePreview() {
  return (
    <div className="lp-preview" aria-label="Illustrative console preview">
      <div className="lp-preview-head">
        <span className="lp-preview-tag">Illustrative</span>
        <span className="mono">jquery@3.4.0 · client portal</span>
      </div>
      <div className="lp-preview-prio">
        <span className="lp-chip lp-chip-critical">Act now</span>
        <span className="lp-chip lp-chip-kev">CISA KEV</span>
      </div>
      <dl className="lp-preview-facts">
        <div>
          <dt>CVE</dt>
          <dd className="mono">CVE-2020-11023</dd>
        </div>
        <div>
          <dt>CVSS 3.1 · NVD</dt>
          <dd className="mono">6.1 Medium</dd>
        </div>
        <div>
          <dt>Weakness</dt>
          <dd>CWE-79 · OWASP A03:2021</dd>
        </div>
        <div>
          <dt>Fixed in</dt>
          <dd className="mono">3.5.0 · OSV</dd>
        </div>
      </dl>
      <p className="lp-preview-ai">
        <span className="label">Agent analysis</span>
        Upgrade jQuery to 3.5.0. CVE-2020-11023 is in CISA KEV, so treat it as urgent.
      </p>
    </div>
  );
}

export function LandingPage() {
  return (
    <div className="lp">
      <header className="lp-nav">
        <div className="lp-container lp-nav-inner">
          <Logo />
          <nav aria-label="Primary" className="lp-links">
            <a href="#what">Platform</a>
            <a href="#how">How it works</a>
            <a href="#benefits">Benefits</a>
            <a href="#control">Control</a>
          </nav>
          <div className="lp-actions">
            <ThemeToggle />
            <Link to="/login" className="btn btn-outline">
              Log in
            </Link>
            <a href="#contact" className="btn btn-primary">
              Book a demo
            </a>
          </div>
        </div>
      </header>

      <main>
        {/* Slide 1 — the title */}
        <section className="lp-container lp-hero">
          <Plus className="lp-plus-tl" />
          <div className="lp-hero-copy">
            <ArrowTag>From findings to decisions</ArrowTag>
            <div className="lp-hero-title">
              <Beam side="left" />
              <h1 className="display">
                Know your exposure <span className="lp-nowrap">before</span> attackers do
              </h1>
            </div>
            <p className="lp-lead">
              Guardian AI is a security twin for regulated financial institutions. Specialist agents
              test the applications you authorise, map the cloud they run on, and explain every
              finding against CVE, CVSS, EPSS and CISA KEV. People approve every step that matters.
            </p>
            <div className="lp-cta-row">
              <a href="#contact" className="btn btn-primary btn-lg">
                Book a demo
              </a>
              <a href="#how" className="btn btn-outline btn-lg">
                See how it works
              </a>
            </div>
          </div>
          <div className="lp-hero-visual">
            <span className="lp-fadebox lp-fadebox-hero" aria-hidden="true" />
            <div className="lp-chamfer">
              <ConsolePreview />
            </div>
            <p className="lp-built-for">
              Built for
              <span className="label">Security teams at banks and wealth managers</span>
            </p>
          </div>
          <Plus className="lp-plus-br" />
        </section>

        <section className="lp-container lp-standards" aria-label="Standards Guardian works with">
          <span className="eyebrow">Grounded in</span>
          <ul>
            {standards.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </section>

        {/* Slide 2 — what it is */}
        <section id="what" className="lp-container lp-section lp-what">
          <div className="lp-what-side">
            <ArrowTag>A twin built to pursue one goal: fewer surprises</ArrowTag>
            <Beam side="left" />
            <Plus />
          </div>
          <div className="lp-what-main">
            <h2 className="display lp-h2">What is a security twin?</h2>
            <p className="lp-body">
              A living model of your applications and the cloud behind them. It observes what is
              exposed, reads what the authoritative sources say about it, and proposes what to do —
              then waits for a person to decide.
            </p>
            <div className="lp-panel lp-triad">
              <div>
                <span className="label">You</span>
                <p>“Check the client portal before Friday’s release.”</p>
              </div>
              <div>
                <span className="label">Guardian</span>
                <ul className="lp-bullets">
                  <li>Scans only the approved hosts</li>
                  <li>Maps the endpoints and the cloud behind them</li>
                  <li>Grades each finding against KEV, EPSS and CVSS</li>
                </ul>
              </div>
              <div>
                <span className="label">Result</span>
                <p>A prioritised, evidence-backed report you can defend.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Slide 3 — how it works */}
        <section id="how" className="lp-container lp-section lp-how">
          <div className="lp-how-left">
            <div className="lp-panel lp-flowbox">
              <ArrowTag>A four-step process, with you at the gates</ArrowTag>
              <Beam side="right" />
            </div>
            <h2 className="display lp-h2 lp-h2-xl">How Guardian works</h2>
          </div>
          <div className="lp-how-right">
            <p className="lp-body">
              Every engagement follows the same cycle, and the lifecycle is enforced in code: an
              agent can propose the next step, but only an approval moves past a gate.
            </p>
            <span className="lp-fadebox" aria-hidden="true" />
            <NumberedList items={steps} />
            <Plus className="lp-plus-end" />
          </div>
        </section>

        {/* Slide 4 — why now */}
        <section className="lp-container lp-section lp-why">
          <div className="lp-why-left">
            <span className="label">Three major changes</span>
            <ol className="lp-plainlist">
              {changes.map((c) => (
                <li key={c.n}>
                  <strong>
                    {c.n} — {c.title}
                  </strong>
                  <span>{c.body}</span>
                </li>
              ))}
            </ol>
            <div className="lp-formula">
              <Beam side="top" />
              <span className="label">Simple formula</span>
              <p>Approved scanning + live intelligence + human sign-off</p>
              <p>= Security you can defend</p>
            </div>
          </div>
          <div className="lp-why-right">
            <h2 className="display lp-h2">Why a twin, why now?</h2>
            <div className="lp-fadebox-card">
              <ArrowTag>Attack surfaces are moving faster than reviews</ArrowTag>
            </div>
            <p className="lp-body">
              Point-in-time testing leaves months between what you tested and what you run. A twin
              keeps the picture current, and keeps a person on every decision.
            </p>
          </div>
        </section>

        {/* Slide 5 — where it helps */}
        <section className="lp-container lp-section lp-where">
          <span className="lp-fadebox lp-fadebox-where" aria-hidden="true" />
          <h2 className="display lp-h2 lp-center">Where Guardian helps</h2>
          <ArrowTag>Routine checks become continuous</ArrowTag>
          <div className="lp-where-grid">
            <div className="lp-where-insight">
              <p className="lp-body">
                Guardian covers the work that is repetitive for people and easy to fall behind on.
              </p>
              <span className="label">Key insight</span>
              <p>Less triage noise → more time on what attackers will actually use</p>
            </div>
            <div className="lp-chamfer lp-chamfer-wide">
              <div className="lp-mapart" aria-hidden="true">
                {Array.from({ length: 24 }, (_, i) => (
                  <span key={i} className={i % 7 === 3 ? 'is-hot' : i % 5 === 0 ? 'is-warm' : ''} />
                ))}
              </div>
              <Beam side="top" />
            </div>
            <div className="lp-where-examples">
              <span className="label">Examples</span>
              {helps.map((h) => (
                <div key={h.label}>
                  <p>{h.label}</p>
                  <ul className="lp-bullets">
                    {h.items.map((i) => (
                      <li key={i}>{i}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Slide 6 — benefits */}
        <section id="benefits" className="lp-container lp-section lp-benefits">
          <div className="lp-benefits-left">
            <Beam side="top" />
            <div className="lp-panel lp-panel-outline">
              <NumberedList items={benefits} />
            </div>
            <Plus />
          </div>
          <div className="lp-benefits-right">
            <p className="lp-body">
              Used well, a security twin makes testing continuous, findings explainable and evidence
              automatic.
            </p>
            <div className="lp-panel lp-panel-outline lp-compare">
              <span className="label">Simple comparison</span>
              <ul className="lp-bullets">
                {comparison.map(([from, to]) => (
                  <li key={from}>
                    {from} → {to}
                  </li>
                ))}
              </ul>
            </div>
            <ArrowTag>Faster, clearer and easier to prove</ArrowTag>
            <h2 className="display lp-h2 lp-h2-xl">The benefits</h2>
          </div>
        </section>

        {/* Slide 7 — humans + agents */}
        <section id="control" className="lp-container lp-section lp-humans">
          <div className="lp-what-side">
            <ArrowTag>The future is collaboration</ArrowTag>
            <Beam side="left" />
            <Plus />
          </div>
          <div className="lp-what-main">
            <h2 className="display lp-h2">Humans + security agents</h2>
            <p className="lp-body">
              The strongest security comes from machine speed combined with human judgement. Agents
              propose; the backend enforces the rules; people decide.
            </p>
            <div className="lp-panel lp-triad">
              <div>
                <span className="label">Agents are good at</span>
                <ul className="lp-bullets">
                  <li>Crawling and observing</li>
                  <li>Reading NVD, EPSS, KEV and OSV</li>
                  <li>Drafting triage with citations</li>
                </ul>
              </div>
              <div>
                <span className="label">People are good at</span>
                <ul className="lp-bullets">
                  <li>Judgement and context</li>
                  <li>Accepting risk</li>
                  <li>Signing off</li>
                </ul>
              </div>
              <div>
                <span className="label">The workflow</span>
                <p>A person approves the scope</p>
                <p>Agents scan and analyse</p>
                <p>A person decides and signs</p>
              </div>
            </div>
          </div>
        </section>

        {/* Slide 8 — the future, and contact */}
        <section id="contact" className="lp-container lp-section lp-final">
          <div className="lp-final-left">
            <h2 className="display lp-h2">Start with one application</h2>
            <div className="lp-panel lp-panel-outline lp-final-box">
              <ArrowTag>From yearly reports to a living twin</ArrowTag>
            </div>
            <Plus />
          </div>
          <div className="lp-final-right">
            <Beam side="top" />
            <p className="lp-body">
              Guardian becomes part of how your teams release software: approved, continuous, and
              explained in the language your auditors already use.
            </p>
            <div className="lp-final-cols">
              <div>
                <span className="label">The evolution</span>
                <p>
                  <strong>Yesterday</strong>A penetration test, once a year.
                </p>
                <p>
                  <strong>Today</strong>Approved, continuous scanning with cited intelligence.
                </p>
                <p>
                  <strong>Next</strong>Authenticated scans and dependency-level CVE matching.
                </p>
              </div>
              <div>
                <span className="label">Get in touch</span>
                <ul className="lp-bullets">
                  <li>[CONTACT_EMAIL]</li>
                  <li>[PHONE]</li>
                </ul>
                <div className="lp-cta-row">
                  <a href="mailto:[CONTACT_EMAIL]" className="btn btn-primary">
                    Book a demo
                  </a>
                  <Link to="/login" className="btn btn-outline">
                    Log in
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="lp-footer">
        <div className="lp-container lp-footer-inner">
          <div className="lp-footer-brand">
            <Logo size={24} />
            <span>© [YEAR] [COMPANY NAME] · For authorised security testing only</span>
          </div>
          <nav aria-label="Footer">
            <a href="#">Security</a>
            <a href="#">Privacy</a>
            <a href="#">Responsible use</a>
            <a href="#">Status</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
